import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import "react-datepicker/dist/react-datepicker.css";

import Dashboard from "./pages/Dashboard";
import Drivers from "./pages/Drivers";
import Admins from "./pages/Admins";
import Rides from "./pages/Rides";
import Vehicles from "./pages/Vehicles";
import Payments from "./pages/Payments";
import Login from "./pages/Login";
import AdminPrivateRoute from "./components/AdminPrivateRoute";
import DriverPrivateRoute from "./components/Driver/DriverPrivateRoute";
import DriverDetail from "./pages/DriverDetail";
import Users from "./pages/Users";
import UserDetail from "./pages/UserDetail";
import VehicleTypes from "./pages/VehicleTypes";
import Brands from "./pages/Brands";
import Employees from "./pages/Employees";
import AddEmployee from "./pages/AddEmployee";
import NewEmployees from "./pages/NewEmployees";
import AddNewEmployee from "./pages/AddNewEmployee";
import EditNewEmployee from "./pages/EditNewEmployee";
import Settings from "./pages/Settings";
import Dropdown from "./components/Dropdown";
import Notification from "./components/Notification";
import ToggleSidebar from "./components/ToggleSidebar";
import Roles from "./pages/Roles";
import AddAdmin from "./pages/AddAdmin";
import Issues from "./pages/Issues";
import RateCard from "./pages/RateCard";
import FaceLiveness from "./pages/FaceLiveness";
import RequestOTP from "./pages/Driver/RequestOTP";
import AdminSidebar from "./components/AdminSidebar";
import UserSidebar from "./components/Driver/UserSidebar";
import VerifyOTP from "./pages/Driver/VerifyOTP";
import UserDashboard from "./pages/Driver/UserDashboard";
import UploadProfilePic from "./pages/Driver/UploadProfilePic";
import VerifyLicense from "./pages/Driver/VerifyLicense";
import VerifyPancard from "./pages/Driver/VerifyPancard";
import VerifyAaadhar from "./pages/Driver/VerifyAaadhar";
import DrivingChoices from "./pages/Driver/DrivingChoices";
import UploadDrivingVideos from "./pages/Driver/UploadDrivingVideos";
import AddDriver from "./pages/AddDriver";
import CreateDriverStart from "./pages/CreateDriverStart";
import Profile from "./pages/Profile";
import { ProfileProvider } from "./context/ProfileContext";
import VerifyBankAccount from "./pages/Driver/VerifyBankAccount";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ChangePassword from "./pages/ChangePassword";
import EmployeeProfile from "./pages/EmployeeProfile";
import Home from "./pages/Home";
import MyAssignedIssues from "./pages/MyAssignedIssues";
import RideStatistics from "./pages/RideStatistics";
import SubscriptionManagement from "./pages/SubscriptionManagement";
import TaxCommission from "./pages/TaxCommission";
import JoiningFee from "./pages/JoiningFee";
import CallBlasting from "./pages/CallBlasting";
import UpdateRideStartedAt from "./pages/UpdateRideStartedAt";
import TripsMetrics from "./pages/TripsMetrics";
import DriversMetrics from "./pages/DriversMetrics";
import DuplicatePanDrivers from "./pages/DuplicatePanDrivers";
import DuplicateAadhaarDrivers from "./pages/DuplicateAadhaarDrivers";
import DuplicateDrivingLicenseDrivers from "./pages/DuplicateDrivingLicenseDrivers";
import AvailableDrivers from "./pages/AvailableDrivers";
import CancellationSuspendedCustomers from "./pages/CancellationSuspendedCustomers";
import CancellationSuspendedDrivers from "./pages/CancellationSuspendedDrivers";
import NumberSharingSuspendedCustomers from "./pages/NumberSharingSuspendedCustomers";
import NumberSharingSuspendedDrivers from "./pages/NumberSharingSuspendedDrivers";
import { detailedReportsPaymentStatus } from "./config/config";

const CallBlastingStatus = React.lazy(() =>
  import("./pages/callBlasting/CallBlastingStatus")
);
const CallBlastingFlow = React.lazy(() =>
  import("./pages/callBlasting/CallBlastingFlow")
);
const CallBlastingLocation = React.lazy(() =>
  import("./pages/callBlasting/CallBlastingLocation")
);
const CallBlastingJoiningFeePending = React.lazy(() =>
  import("./pages/callBlasting/CallBlastingJoiningFeePending")
);
const CallBlastingUsersRegistered = React.lazy(() =>
  import("./pages/callBlasting/CallBlastingUsersRegistered")
);

function App() {
  return (
    <ProfileProvider>
      <Router>
        <AppRoutes />
        <ToastContainer position="top-right" autoClose={3000} />
      </Router>
    </ProfileProvider>
  );
}

const AppRoutes = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const currentPath = location.pathname;

  const adminPaths = [
    "/dashboard",
    "/admins",
    "/drivers",
    "/users",
    "/employees",
    "/employees/new",
    "/employees/new-add",
    "/vehicles",
    "/payments",
    "/vehicle-types",
    "/vehicles/brands",
    "/settings",
    "/roles",
    "/issues",
    "/my-assigned-issues",
    "/ratecard",
    "/drivers/detail",
    "/drivers/duplicate-pan",
    "/drivers/duplicate-aadhaar",
    "/drivers/duplicate-dl",
    "/profile",
    "/change-password",
    "/rides",
    "/ride-statistics",
    "/cancellation-suspended/customers",
    "/cancellation-suspended/drivers",
    "/number-sharing-suspended/customers",
    "/number-sharing-suspended/drivers",
    "/available-drivers",
    "/subscription-management",
    "/tax-commission",
    "/joining-fee",
    "/call-blasting",
    ...(detailedReportsPaymentStatus ? ["/ride-update-start-time"] : []),
  ];

  const userPaths = [
    "/user/dashboard",
    "/user/profile",
    "/user/rides",
    "/user/wallet",
    // Add any other exact user routes here
  ];

  const isAdminRoute = adminPaths.some((base) => {
    const regex = new RegExp(`^${base}(/.*)?$`); // match base or base + subpath
    return regex.test(currentPath);
  });
  const isUserRoute = userPaths.includes(currentPath);

  useEffect(() => {
    const timer = setTimeout(() => {}, 500);
    return () => clearTimeout(timer);
  }, [location]);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Sidebars */}
      {isAdminRoute && <AdminSidebar open={sidebarOpen} />}
      {isUserRoute && <UserSidebar open={sidebarOpen} />}

      {/* Main Content */}
      <div className="flex flex-col flex-1 w-full overflow-y-auto overflow-x-hidden">
        {/* Header */}
        {(isAdminRoute || isUserRoute) && (
          <header className="sticky top-0 z-40 flex w-full bg-white shadow-md justify-between items-center px-4 py-3">
            <ToggleSidebar click={toggleSidebar} />
            <div className="flex items-center space-x-4">
              <Notification />
              <Dropdown />
            </div>
          </header>
        )}

        {/* Routes */}
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/face-liveness" element={<FaceLiveness />} />
          <Route path="/login" element={<Login />} />
          <Route path="/request-otp" element={<RequestOTP />} />
          <Route path="/verify-otp" element={<VerifyOTP />} />
          {/* User Protected Routes */}
          <Route
            path="/user-dashboard"
            element={
              <DriverPrivateRoute>
                <UserDashboard />
              </DriverPrivateRoute>
            }
          />
          <Route
            path="/upload-profile"
            element={
              <DriverPrivateRoute>
                <UploadProfilePic />
              </DriverPrivateRoute>
            }
          />
          <Route
            path="/verify-license"
            element={
              <DriverPrivateRoute>
                <VerifyLicense />
              </DriverPrivateRoute>
            }
          />
          <Route
            path="/verify-pan"
            element={
              <DriverPrivateRoute>
                <VerifyPancard />
              </DriverPrivateRoute>
            }
          />
          <Route
            path="/verify-bank"
            element={
              <DriverPrivateRoute>
                <VerifyBankAccount />
              </DriverPrivateRoute>
            }
          />

          <Route
            path="/verify-aadhar"
            element={
              <DriverPrivateRoute>
                <VerifyAaadhar />
              </DriverPrivateRoute>
            }
          />
          <Route
            path="/driving-choices"
            element={
              <DriverPrivateRoute>
                <DrivingChoices />
              </DriverPrivateRoute>
            }
          />
          <Route
            path="/driving-videos"
            element={
              <DriverPrivateRoute>
                <UploadDrivingVideos />
              </DriverPrivateRoute>
            }
          />
          {/* Admin Protected Routes */}
          <Route
            path="/dashboard"
            element={
              <AdminPrivateRoute>
                <Dashboard />
              </AdminPrivateRoute>
            }
          />
          <Route
            path="/dashboard/trips-metrics"
            element={
              <AdminPrivateRoute>
                <TripsMetrics />
              </AdminPrivateRoute>
            }
          />
          <Route
            path="/dashboard/drivers-metrics"
            element={
              <AdminPrivateRoute>
                <DriversMetrics />
              </AdminPrivateRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <AdminPrivateRoute>
                <Profile />
              </AdminPrivateRoute>
            }
          />
          <Route
            path="/change-password"
            element={
              <AdminPrivateRoute>
                <ChangePassword />
              </AdminPrivateRoute>
            }
          />
          <Route
            path="/drivers"
            element={
              <AdminPrivateRoute>
                <Drivers />
              </AdminPrivateRoute>
            }
          />
          <Route
            path="/drivers/duplicate-pan"
            element={
              <AdminPrivateRoute>
                <DuplicatePanDrivers />
              </AdminPrivateRoute>
            }
          />
          <Route
            path="/drivers/duplicate-aadhaar"
            element={
              <AdminPrivateRoute>
                <DuplicateAadhaarDrivers />
              </AdminPrivateRoute>
            }
          />
          <Route
            path="/drivers/duplicate-dl"
            element={
              <AdminPrivateRoute>
                <DuplicateDrivingLicenseDrivers />
              </AdminPrivateRoute>
            }
          />
          <Route
            path="/drivers/add"
            element={
              <AdminPrivateRoute>
                <CreateDriverStart />
              </AdminPrivateRoute>
            }
          />
          <Route
            path="/drivers/add/:id"
            element={
              <AdminPrivateRoute>
                <AddDriver />
              </AdminPrivateRoute>
            }
          />
          <Route
            path="/users"
            element={
              <AdminPrivateRoute>
                <Users />
              </AdminPrivateRoute>
            }
          />
          <Route
            path="/users/detail/:id"
            element={
              <AdminPrivateRoute>
                <UserDetail />
              </AdminPrivateRoute>
            }
          />
          <Route
            path="/employees"
            element={
              <AdminPrivateRoute>
                <Employees />
              </AdminPrivateRoute>
            }
          />
          <Route
            path="/employees/profile/:id"
            element={
              <AdminPrivateRoute>
                <EmployeeProfile />
              </AdminPrivateRoute>
            }
          />
          <Route
            path="/employees/add"
            element={
              <AdminPrivateRoute>
                <AddEmployee />
              </AdminPrivateRoute>
            }
          />
          <Route
            path="/employees/new"
            element={
              <AdminPrivateRoute>
                <NewEmployees />
              </AdminPrivateRoute>
            }
          />
          <Route
            path="/employees/new-add"
            element={
              <AdminPrivateRoute>
                <AddNewEmployee />
              </AdminPrivateRoute>
            }
          />
          <Route
            path="/employees/new/edit/:id"
            element={
              <AdminPrivateRoute>
                <EditNewEmployee />
              </AdminPrivateRoute>
            }
          />
          <Route
            path="/admins"
            element={
              <AdminPrivateRoute>
                <Admins />
              </AdminPrivateRoute>
            }
          />
          <Route
            path="/admins/add"
            element={
              <AdminPrivateRoute>
                <AddAdmin />
              </AdminPrivateRoute>
            }
          />
          <Route
            path="/rides"
            element={
              <AdminPrivateRoute>
                <Rides />
              </AdminPrivateRoute>
            }
          />
          <Route
            path="/ride-statistics"
            element={
              <AdminPrivateRoute>
                <RideStatistics />
              </AdminPrivateRoute>
            }
          />
          <Route
            path="/cancellation-suspended/customers"
            element={
              <AdminPrivateRoute>
                <CancellationSuspendedCustomers />
              </AdminPrivateRoute>
            }
          />
          <Route
            path="/cancellation-suspended/drivers"
            element={
              <AdminPrivateRoute>
                <CancellationSuspendedDrivers />
              </AdminPrivateRoute>
            }
          />
          <Route
            path="/number-sharing-suspended/customers"
            element={
              <AdminPrivateRoute>
                <NumberSharingSuspendedCustomers />
              </AdminPrivateRoute>
            }
          />
          <Route
            path="/number-sharing-suspended/drivers"
            element={
              <AdminPrivateRoute>
                <NumberSharingSuspendedDrivers />
              </AdminPrivateRoute>
            }
          />
          <Route
            path="/available-drivers"
            element={
              <AdminPrivateRoute>
                <AvailableDrivers />
              </AdminPrivateRoute>
            }
          />
          {detailedReportsPaymentStatus && (
            <Route
              path="/ride-update-start-time"
              element={
                <AdminPrivateRoute>
                  <UpdateRideStartedAt />
                </AdminPrivateRoute>
              }
            />
          )}
          <Route
            path="/vehicles"
            element={
              <AdminPrivateRoute>
                <Vehicles />
              </AdminPrivateRoute>
            }
          />
          <Route
            path="/vehicle-types"
            element={
              <AdminPrivateRoute>
                <VehicleTypes />
              </AdminPrivateRoute>
            }
          />
          <Route
            path="/vehicles/brands"
            element={
              <AdminPrivateRoute>
                <Brands />
              </AdminPrivateRoute>
            }
          />
          <Route
            path="/payments"
            element={
              <AdminPrivateRoute>
                <Payments />
              </AdminPrivateRoute>
            }
          />
          <Route
            path="/drivers/detail/:id"
            element={
              <AdminPrivateRoute>
                <DriverDetail />
              </AdminPrivateRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <AdminPrivateRoute>
                <Settings />
              </AdminPrivateRoute>
            }
          />
          <Route
            path="/roles"
            element={
              <AdminPrivateRoute>
                <Roles />
              </AdminPrivateRoute>
            }
          />
          <Route
            path="/issues"
            element={
              <AdminPrivateRoute>
                <Issues />
              </AdminPrivateRoute>
            }
          />
          <Route
            path="/my-assigned-issues"
            element={
              <AdminPrivateRoute>
                <MyAssignedIssues />
              </AdminPrivateRoute>
            }
          />
          <Route
            path="/ratecard"
            element={
              <AdminPrivateRoute>
                <RateCard />
              </AdminPrivateRoute>
            }
          />
          <Route
            path="/subscription-management"
            element={
              <AdminPrivateRoute>
                <SubscriptionManagement />
              </AdminPrivateRoute>
            }
          />
          <Route
            path="/tax-commission"
            element={
              <AdminPrivateRoute>
                <TaxCommission />
              </AdminPrivateRoute>
            }
          />
          <Route
            path="/joining-fee"
            element={
              <AdminPrivateRoute>
                <JoiningFee />
              </AdminPrivateRoute>
            }
          />
          <Route
            path="/call-blasting"
            element={
              <AdminPrivateRoute>
                <CallBlasting />
              </AdminPrivateRoute>
            }
          />
          <Route
            path="/call-blasting/status"
            element={
              <AdminPrivateRoute>
                <React.Suspense
                  fallback={
                    <div className="p-6 text-sm text-gray-500">Loading…</div>
                  }
                >
                  <CallBlastingStatus />
                </React.Suspense>
              </AdminPrivateRoute>
            }
          />
          <Route
            path="/call-blasting/flow"
            element={
              <AdminPrivateRoute>
                <React.Suspense
                  fallback={
                    <div className="p-6 text-sm text-gray-500">Loading…</div>
                  }
                >
                  <CallBlastingFlow />
                </React.Suspense>
              </AdminPrivateRoute>
            }
          />
          <Route
            path="/call-blasting/location"
            element={
              <AdminPrivateRoute>
                <React.Suspense
                  fallback={
                    <div className="p-6 text-sm text-gray-500">Loading…</div>
                  }
                >
                  <CallBlastingLocation />
                </React.Suspense>
              </AdminPrivateRoute>
            }
          />
          <Route
            path="/call-blasting/joining-fee-pending"
            element={
              <AdminPrivateRoute>
                <React.Suspense
                  fallback={
                    <div className="p-6 text-sm text-gray-500">Loading…</div>
                  }
                >
                  <CallBlastingJoiningFeePending />
                </React.Suspense>
              </AdminPrivateRoute>
            }
          />
          <Route
            path="/call-blasting/users-registered"
            element={
              <AdminPrivateRoute>
                <React.Suspense
                  fallback={
                    <div className="p-6 text-sm text-gray-500">Loading…</div>
                  }
                >
                  <CallBlastingUsersRegistered />
                </React.Suspense>
              </AdminPrivateRoute>
            }
          />
        </Routes>
      </div>
    </div>
  );
};

export default App;
